import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

import Const from '../Constants';

import {
  handleUpdateTask,
  rankBetween,
  rankCompare
} from '../Utils';

export const useTasksStore = create((set, get) => ({

  // Tasks grouped by status, sorted within each status by `rank`.
  tasks: [],

  // Flat representation used by the catch-up view.
  flatTasks: [],

  // Tasks after applying urgent/important filters, used by the main board.
  filteredTasks: [],

  showCompletedTasks: false,
  showUrgentTasks: false,
  showImportantTasks: false,

  currentEpic: null,

  setShowCompletedTasks: (auth, value) => {
    set({ showCompletedTasks: value });
    get().loadTasks(auth);
  },

  setShowUrgentTasks: (_auth, value) => {
    set({ showUrgentTasks: value });
    get().recomputeDerivedStates();
  },

  setShowImportantTasks: (_auth, value) => {
    set({ showImportantTasks: value });
    get().recomputeDerivedStates();
  },

  setCurrentEpic: (auth, epic) => {
    set({ currentEpic: epic });
    get().loadTasks(auth);
  },

  loadTasks: async (auth) => {
    try {
      const { showCompletedTasks, currentEpic } = get();
      const queryParams = new URLSearchParams({
        includeDone: showCompletedTasks
      });
      if (currentEpic) {
        queryParams.set('filterByEpicId', currentEpic.id);
      }

      const idToken = await auth.currentUser.getIdToken();
      const theURL = `${process.env.REACT_APP_BASE_URL}/tasks?${queryParams}`;

      const response = await fetch(theURL, {
        headers: new Headers({ Authorization: idToken })
      });
      const tasks = await response.json();

      set({ tasks });
      get().recomputeDerivedStates();
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  },

  recomputeDerivedStates: () => {
    try {
      const { tasks } = get();

      const flatTasks = [
        ...(tasks[Const.sTodo] || []),
        ...(tasks[Const.sInProgress] || [])
      ];
      set({ flatTasks });

      const { showUrgentTasks, showImportantTasks } = get();
      const filteredTasks = {};

      Object.keys(tasks || {}).forEach(status => {
        let arr = tasks[status] || [];
        if (showUrgentTasks || showImportantTasks) {
          arr = arr.filter(t => {
            const passesUrgent = !showUrgentTasks || t.urgency === Const.sHigh;
            const passesImportant = !showImportantTasks || t.importance === Const.sHigh;
            return passesUrgent && passesImportant;
          });
        }
        filteredTasks[status] = arr;
      });

      set({ filteredTasks });
    } catch (error) {
      console.error('Error recomputing derived states:', error);
    }
  },

  handleAddTask: async (auth, status) => {
    try {
      const { tasks, currentEpic } = get();

      const column = tasks[status] || [];
      const last = column[column.length - 1];
      const rank = rankBetween(last?.rank ?? null, null);

      const tempId = uuidv4();
      const optimisticTask = {
        id: tempId,
        title: '',
        description: '',
        status,
        epicId: (currentEpic ? currentEpic.id : null),
        rank,
        done: false
      };

      const updatedTasks = { ...tasks, [status]: [...column, optimisticTask] };
      set({ tasks: updatedTasks });
      get().recomputeDerivedStates();

      const idToken = await auth.currentUser.getIdToken();
      const theURL = process.env.REACT_APP_BASE_URL + '/tasks';
      const response = await fetch(theURL, {
        method: Const.HttpPost,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson
        }),
        body: JSON.stringify({
          id: tempId,
          status,
          rank,
          epicId: (currentEpic ? currentEpic.id : null)
        })
      });

      const realTask = await response.json();

      const currentTasks = get().tasks;
      const newStatusArray = (currentTasks[status] || []).map(task =>
        task.id === tempId ? realTask : task
      );
      set({ tasks: { ...currentTasks, [status]: newStatusArray } });
      get().recomputeDerivedStates();
    } catch (error) {
      console.error(error.message);
    }
  },

  // Used by the catch-up view to advance status without explicit positioning.
  // Task keeps its current rank; falls into place in the destination column based on existing ranks.
  changeTaskStatus: async (auth, taskId, oldStatus, status) => {
    try {
      const { tasks } = get();

      handleUpdateTask(auth, taskId, null, null, null, status);

      const task = (tasks[oldStatus] || []).find(t => t.id === taskId);
      if (!task) return;

      const updatedTask = { ...task, status };
      const updatedTasks = {
        ...tasks,
        [oldStatus]: (tasks[oldStatus] || []).filter(t => t.id !== taskId),
        [status]: [...(tasks[status] || []), updatedTask].sort(rankCompare),
      };

      set({ tasks: updatedTasks });
      get().recomputeDerivedStates();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  },

  // Drop a task into a column at the position between two existing tasks.
  // beforeId / afterId may be null to indicate top-of-column or bottom-of-column inserts.
  moveTask: async (auth, { taskId, toStatus, beforeId, afterId }) => {
    try {
      const { tasks } = get();

      let fromStatus = null;
      let task = null;
      for (const s of Object.keys(tasks || {})) {
        const found = (tasks[s] || []).find(t => t.id === taskId);
        if (found) { fromStatus = s; task = found; break; }
      }
      if (!task) return;

      const destColumn = (tasks[toStatus] || []).filter(t => t.id !== taskId);
      const beforeRank = beforeId ? destColumn.find(t => t.id === beforeId)?.rank : null;
      const afterRank = afterId ? destColumn.find(t => t.id === afterId)?.rank : null;
      const newRank = rankBetween(beforeRank ?? null, afterRank ?? null);

      const updatedTask = { ...task, status: toStatus, rank: newRank };
      const nextFrom = (tasks[fromStatus] || []).filter(t => t.id !== taskId);
      const nextDest = [...destColumn, updatedTask].sort(rankCompare);

      const updatedTasks = { ...tasks };
      if (fromStatus === toStatus) {
        updatedTasks[toStatus] = nextDest;
      } else {
        updatedTasks[fromStatus] = nextFrom;
        updatedTasks[toStatus] = nextDest;
      }

      set({ tasks: updatedTasks });
      get().recomputeDerivedStates();

      const statusForBackend = fromStatus === toStatus ? null : toStatus;
      try {
        await handleUpdateTask(auth, taskId, null, null, null, statusForBackend, newRank);
      } catch (e) {
        console.error('moveTask backend update failed, reloading:', e);
        await get().loadTasks(auth);
      }
    } catch (error) {
      console.error('Error moving task:', error);
    }
  },

  handleUpdateTask_: async (
    auth,
    taskId,
    title = null,
    description = null,
    done = null,
    status = null,
    rank = null,
    epicId = null,
    importance = null,
    urgency = null,
    assigneeType = undefined,
    assigneeId = undefined
  ) => {
    try {
      const { tasks } = get();
      const updatedTasks = { ...tasks };

      const task = Object.values(tasks).flat().find(t => t.id === taskId);
      if (!task) {
        await handleUpdateTask(auth, taskId, title, description, done, status, rank, epicId, importance, urgency, assigneeType, assigneeId);
        return;
      }

      if (title !== null && task.title !== title) task.title = title;
      if (done !== null && task.done !== done) task.done = done;
      if (epicId !== null) task.epicId = epicId;
      if (importance !== null) task.importance = importance;
      if (urgency !== null) task.urgency = urgency;
      if (assigneeType !== undefined) task.assigneeType = assigneeType;
      if (assigneeId !== undefined) task.assigneeId = assigneeId;

      // Cross-column status change without an explicit rank: keep current rank,
      // re-sort the destination column. (The destination will surface the task
      // wherever its rank falls; matches prior order-based behavior.)
      if (status !== null && task.status !== status) {
        const oldStatus = task.status;
        task.status = status;
        if (rank !== null) task.rank = rank;
        updatedTasks[oldStatus] = (updatedTasks[oldStatus] || []).filter(t => t.id !== taskId);
        updatedTasks[status] = [...(updatedTasks[status] || []), task].sort(rankCompare);
      } else if (rank !== null && task.rank !== rank) {
        task.rank = rank;
        const s = task.status;
        updatedTasks[s] = [...(updatedTasks[s] || [])].sort(rankCompare);
      }

      set({ tasks: updatedTasks });
      get().recomputeDerivedStates();

      handleUpdateTask(auth, taskId, title, description, done, status, rank, epicId, importance, urgency, assigneeType, assigneeId);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  },

  handleDeleteTask: async (auth, taskId) => {
    try {
      const { tasks } = get();
      const updatedTasks = { ...tasks };
      for (const status in updatedTasks) {
        updatedTasks[status] = updatedTasks[status].filter(task => task.id !== taskId);
      }
      set({ tasks: updatedTasks });
      get().recomputeDerivedStates();

      const idToken = await auth.currentUser.getIdToken();
      const theURL = process.env.REACT_APP_BASE_URL + '/tasks';
      await fetch(theURL, {
        method: Const.HttpDelete,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson
        }),
        body: JSON.stringify({ taskId })
      });
    } catch (error) {
      console.error(error.message);
    }
  }

}));
